import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { startOfTodayLocal } from "@/lib/celebrationDates";

export interface CelebrationRow {
  id: string;
  date: string;
  type: string;
  status: string;
  // MS78: dietary_requirements joined in so Dashboard's
  // recommendationFor can pass them to the engine. Was stubbed to []
  // before, meaning the engine never honoured a recipient's dietary
  // needs when picking a SKU for the up-next list. Optional/nullable
  // because legacy team_member rows may have it as null + a few callers
  // pass demo data without the field.
  //
  // MS79: tier_override added so per-recipient tier customisation flows
  // through to the engine (a teammate explicitly marked as the
  // "luxury" tier on /team/:id/profile gets that tier respected on
  // every Dashboard recommendation, instead of the company-default).
  team_members:
    | {
        // MS82: id added so Dashboard's Adjust → /chat handler can
        // pre-load recipientId for chat-concierge's get_team_member
        // tool (lets the LLM hit the recipient's full profile on
        // turn 1 rather than asking the buyer to disambiguate by
        // name).
        id?: string;
        name: string;
        dietary_requirements?: string[] | null;
        tier_override?: string | null;
        // MS569-followup-A: start_date threaded through so Dashboard's
        // Adjust → /chat handler can use the canonical celebrationToChatUrl
        // helper (which computes work-anniversary year-count phrasing from
        // start_date). Pre-followup the Dashboard had its own URL composer
        // because this field was absent from the select, forcing
        // Calendar / Notifications / Orders onto one helper and Dashboard
        // onto a specialised one. Adding the column here lets all four
        // surfaces converge.
        start_date?: string | null;
      }
    | {
        id?: string;
        name: string;
        dietary_requirements?: string[] | null;
        tier_override?: string | null;
        start_date?: string | null;
      }[]
    | null;
}

interface DateRange {
  from: string;
  to: string;
}

/**
 * Count of celebrations for a company inside a date range. Used by dashboard
 * stats (this-month + next-7-days counts).
 */
export function useCelebrationCount(
  companyId: string | null | undefined,
  range: DateRange,
) {
  return useQuery({
    queryKey: ["celebrations", companyId, "count", range.from, range.to],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<number> => {
      if (!companyId) return 0;
      const { count, error } = await supabase
        .from("celebrations")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId)
        .gte("date", range.from)
        .lte("date", range.to);
      if (error) throw error;
      return count ?? 0;
    },
  });
}

export interface CelebrationRecord {
  id: string;
  company_id: string;
  team_member_id: string;
  type: string;
  date: string;
  status: string;
}

/**
 * Full celebrations list for a company, ordered by date asc. Used by the
 * /calendar page which needs to see every approved, upcoming, and skipped
 * celebration. Member names are joined client-side in the caller from the
 * existing useTeamMembers cache so we don't refetch the same names twice.
 * Callers mutating celebration rows should invalidate
 * ["celebrations", companyId] to refresh everything on this prefix in
 * one call.
 */
export function useAllCelebrations(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["celebrations", companyId, "all"],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<CelebrationRecord[]> => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("celebrations")
        .select("id, company_id, team_member_id, type, date, status")
        .eq("company_id", companyId)
        .order("date", { ascending: true });
      if (error) throw error;
      return (data as CelebrationRecord[] | null) ?? [];
    },
  });
}

// Sprint 2.5a /orders-new-redirect, over-selects member.id +
// start_date so callers can compose a /chat brief via
// celebrationToChatUrl without a second round-trip. Backwards-
// compatible: existing consumers reading just .name still work.
export interface ApprovedCelebrationLineItem {
  sku_id?: string;
  name?: string;
  price_aud?: number;
  quantity?: number;
}

export interface ApprovedCelebrationRecommendation {
  id: string;
  line_items: ApprovedCelebrationLineItem[] | null;
  total_cents: number | null;
  status?: string;
  approved_at?: string | null;
}

export interface ApprovedCelebrationRow {
  id: string;
  date: string;
  type: string;
  status?: string;
  team_member_id: string;
  team_members:
    | {
        id: string;
        name: string;
        start_date: string | null;
        dietary_requirements?: string[] | null;
      }
    | {
        id: string;
        name: string;
        start_date: string | null;
        dietary_requirements?: string[] | null;
      }[]
    | null;
  /** Latest approved recommendation with SKU context for fulfilment. */
  approved_recommendation?: ApprovedCelebrationRecommendation | null;
}

/**
 * Celebrations the user has approved but hasn't yet created an order for.
 * Surfaced on the orders page as an "Action needed" list so these don't
 * get stranded between approval and checkout. Matches on the celebration
 * status field, OrderCreate flips it to "ordered" after a successful
 * order create, so "approved" here means exactly "approved and still
 * waiting on an order".
 */
export function useApprovedCelebrationsPendingOrder(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["celebrations", companyId, "approved-pending-order"],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<ApprovedCelebrationRow[]> => {
      if (!companyId) return [];
      const todayStr = format(startOfTodayLocal(), "yyyy-MM-dd");
      const { data, error } = await supabase
        .from("celebrations")
        .select(
          `id, date, type, status, team_member_id,
           team_members(id, name, start_date, dietary_requirements),
           celebration_recommendations(id, line_items, total_cents, status, approved_at)`,
        )
        .eq("company_id", companyId)
        .eq("status", "approved")
        .gte("date", todayStr)
        .order("date", { ascending: true });
      if (error) throw error;

      return ((data ?? []) as Array<
        ApprovedCelebrationRow & {
          celebration_recommendations?:
            | ApprovedCelebrationRecommendation[]
            | ApprovedCelebrationRecommendation
            | null;
        }
      >).map((row) => {
        const recs = row.celebration_recommendations;
        const recList = Array.isArray(recs) ? recs : recs ? [recs] : [];
        const approvedRec =
          recList
            .filter((r) => r.status === "approved")
            .sort((a, b) => {
              const aTime = a.approved_at ?? "";
              const bTime = b.approved_at ?? "";
              return bTime.localeCompare(aTime);
            })[0] ?? null;

        const { celebration_recommendations: _drop, ...rest } = row;
        return {
          ...rest,
          approved_recommendation: approvedRec
            ? {
                id: approvedRec.id,
                line_items: approvedRec.line_items as ApprovedCelebrationLineItem[] | null,
                total_cents: approvedRec.total_cents,
              }
            : null,
        };
      });
    },
  });
}

/**
 * Upcoming celebrations with team-member name joined in. Used by the
 * dashboard "Coming up" widget. Filters to the non-finalised statuses so
 * delivered and cancelled entries don't clutter the feed.
 */
export function useUpcomingCelebrations(
  companyId: string | null | undefined,
  options: { from: string; to: string; limit?: number } = { from: "", to: "" },
) {
  const { from, to, limit = 5 } = options;
  return useQuery({
    queryKey: ["celebrations", companyId, "upcoming", from, to, limit],
    enabled: Boolean(companyId && from && to),
    queryFn: async (): Promise<CelebrationRow[]> => {
      if (!companyId) return [];
      // MS78: select team_members.dietary_requirements alongside name
      // so the Dashboard's recommendationFor adapter can pass them
      // to the engine. MS79: tier_override added for the same reason
      // — engine input now receives the per-recipient tier when set,
      // falling back to company default → "standard" otherwise.
      // MS569-followup-A: start_date added so the Dashboard Adjust →
      // /chat handler can route through celebrationToChatUrl (the
      // canonical helper) instead of the specialised buildDashboardAdjustUrl.
      // celebrationToChatUrl uses start_date to compute the natural-
      // language year-count phrasing for work anniversaries
      // ("Sarah's 4-year work anniversary on 12 May"). One extra column
      // on an existing join — no perf cost.
      const { data, error } = await supabase
        .from("celebrations")
        .select(
          "id, date, type, status, team_members(id, name, dietary_requirements, tier_override, start_date)",
        )
        .eq("company_id", companyId)
        .gte("date", from)
        .lte("date", to)
        .in("status", ["upcoming", "approved", "ordered"])
        .order("date", { ascending: true })
        .limit(limit);
      if (error) throw error;
      return (data as CelebrationRow[] | null) ?? [];
    },
  });
}

/**
 * Skipped celebrations inside a date range. Used by the Dashboard
 * "Skipped" filter chip — the main upcoming feed excludes these rows.
 */
export function useSkippedCelebrations(
  companyId: string | null | undefined,
  options: { from: string; to: string; limit?: number } = { from: "", to: "" },
) {
  const { from, to, limit = 25 } = options;
  return useQuery({
    queryKey: ["celebrations", companyId, "skipped", from, to, limit],
    enabled: Boolean(companyId && from && to),
    queryFn: async (): Promise<CelebrationRow[]> => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("celebrations")
        .select(
          "id, date, type, status, team_members(id, name, dietary_requirements, tier_override, start_date)",
        )
        .eq("company_id", companyId)
        .eq("status", "skipped")
        .gte("date", from)
        .lte("date", to)
        .order("date", { ascending: true })
        .limit(limit);
      if (error) throw error;
      return (data as CelebrationRow[] | null) ?? [];
    },
  });
}
